import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { dashboardApi, dashFilterExtra } from '../../services/dashboard-api.service';
import FilterBar from '../../components/dashboard-report/FilterBar';
import SectionTable from '../../components/dashboard-report/SectionTable';
import type { PredBriefReport as PredBriefReportType, DashboardFilters } from '../../types/dashboard.types';

const PredBriefReport: React.FC = () => {
  const [data, setData] = useState<PredBriefReportType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<DashboardFilters>({ grant: 'All' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { grant, districtId, mandalId, schemeType } = filters;
      const g = grant === 'All' ? undefined : grant;
      const report = await dashboardApi.getPredBriefReport(g, districtId, mandalId, schemeType, dashFilterExtra(filters));
      setData(report);
    } catch (err: any) {
      setError(err.message || 'Failed to load PRED Brief Report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const { grant, districtId, mandalId } = filters;
      await dashboardApi.exportExcel(grant === 'All' ? undefined : grant, districtId, mandalId);
    } catch {
      setError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Panchayat Raj Engineering Department</h1>
        <p className="text-sm text-gray-500">
          PRED Brief Report — Data as on {data?.as_on_date || new Date().toISOString().split('T')[0]}
        </p>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
        onExport={handleExport}
        onRefresh={loadData}
        loading={loading}
        showSchemeType
        exporting={exporting}
      />

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
          <button onClick={loadData} className="text-red-600 hover:text-red-800 underline text-sm">Retry</button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
          <span className="text-gray-600">Loading report data...</span>
        </div>
      )}

      {/* Report Sections */}
      {!loading && data && (
        <>
          <SectionTable
            title="On Hand Programme / Works in Progress for the current year"
            data={data.on_hand_programme}
            color="blue"
          />

          <SectionTable
            title="Achievements during the Year"
            data={data.achievements_current_fy}
            color="green"
          />

          <SectionTable
            title="Balance"
            data={data.balance}
            color="orange"
          />

          <SectionTable
            title="Achievements Upto Last FY"
            data={data.achievements_upto_last_fy}
            color="purple"
          />

          {/* Habitation Connected Status */}
          <div className="mb-6">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-semibold">Habitation Connected Status with BT/CC</h3>
            </div>
            <div className="overflow-x-auto border border-t-0 border-gray-200 rounded-b-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Population Category</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700">No of Habitations</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700">Connected</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700">Balance to be connected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.habitation_status.categories.map((cat) => (
                    <tr key={cat.population_range} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{cat.population_range}</td>
                      <td className="px-4 py-2 text-right">{cat.total_habitations}</td>
                      <td className="px-4 py-2 text-right">{cat.connected}</td>
                      <td className="px-4 py-2 text-right">{cat.balance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.habitation_status._note && (
                <p className="px-4 py-2 text-xs text-gray-400 italic">{data.habitation_status._note}</p>
              )}
            </div>
          </div>

          {/* Unawarded Status + QC Inspections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Unawarded Status</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{data.unawarded_status.total_unawarded}</p>
                  <p className="text-xs text-gray-500">Total Unawarded Works</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{data.unawarded_status.estimate_cost.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Estimate Cost (Lakhs)</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">QC Inspections</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{data.qc_inspections.works_to_be_inspected}</p>
                  <p className="text-xs text-gray-500">To be Inspected</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{data.qc_inspections.works_inspected}</p>
                  <p className="text-xs text-gray-500">Inspected</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{data.qc_inspections.pending_inspection}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </div>
          </div>

          {/* Definitions */}
          <div className="bg-gray-100 rounded-lg p-4 text-xs text-gray-500">
            <p className="font-semibold mb-1">Definitions:</p>
            <p>Cost in Lakhs | Road length in KMs | Nos are in Numbers</p>
          </div>
        </>
      )}
    </div>
  );
};

export default PredBriefReport;
